// This file's purpose is to hold all expected error messages that we want to have specific behavior for

import { projectConfig } from "../project-info/projectConfig";
import { MAX_COMMENT_LENGTH, MAX_POST_LENGTH } from "./Restrictions";

const STREAM_ERRORS = {
  ALREADY_REACTED: 'You have already reacted to this post.',
  REACTION_NOT_FOUND: 'The reaction was not found.',
  FEED_RETRIEVAL: 'There was an error retrieving your feed.',
  USER_FEED: 'Error retrieving the user feed',
  EVENT_FEED: 'There was an error retrieving the event feed.',
  POST_DELETION: 'Error deleting the post. Please try again later.',
  ALREADY_LIKED: "You've already liked this post. Please refresh your feed.",
  ALREADY_UNLIKED:
    "You've already unliked this post or it was deleted. Please refresh your feed.",
  UNLIKING: 'Error unliking post. Please try again later.',
  LIKING: 'Error liking post. Please try again later.',
  POST_RETRIEVAL: 'Error retrieving post',
  MORE_COMMENTS: 'Error loading more comments. Try again later.',
  REFRESHING_POST: 'Error refreshing post. Please try again later.',
  FOLLOWERS_LIST: 'Unable to retrieve the list this user is following.',
  FOLLOWING_LIST: 'Unable to retrieve the list of users following this user.',
  UNFOLLOWING_USER: 'Error unfollowing user, please try again later.',
  FOLLOWING_USER: 'Error following user, please try again later.',
  RETRIEVING_NOTIFICATIONS: 'Error retrieving notifications',
  GROUP_FEED:
    'An error occurred trying to retrieve the group feed. Please try again later.',
  EMPTY_POST: 'Posts cannot be blank.',
  UPLOAD_POST: `Unable to upload post to ${projectConfig.name} Servers.`,
  UPDATE_POST: `Unable to update post to ${projectConfig.name} Servers.`,
  EMPTY_COMMENT: 'Unable to post an empty comment.',
  UPDATE_COMMENT: 'Unable to update your comment. Please try again later.',
  UPLOAD_COMMENT: 'Unable to post your comment. Please try again later.',
  POST_IMAGE: `Error uploading post image to ${projectConfig.name} Servers.`,
  EXCEEDED_MAX_POST_LENGTH: `Post has exceeded ${MAX_POST_LENGTH} characters.`,
  EXCEEDED_MAX_COMMENT_LENGTH: `Comment has exceeded ${MAX_COMMENT_LENGTH} characters.`,
};

const LOAD_PROFILE_ERROR = 'Unable to load profile.';

const REPORT_ERROR = 'There was an error sending your report. Please try again later.';

const POST_BLOCK_ERROR = 'Error blocking the post. Please try again later.';

const POST_DELETE_ERROR = 'Error deleting the post. Please try again later.';

const GET_VERSION_ERROR = 'Unable to retrieve version number.';

const NO_USERS_FOUND = 'No Users Found';

const NO_GROUPS_FOUND = 'No groups matching search term.';

const NO_GROUPS_TIP = 'If searching for groups by location, please use zip or "city, state" format in the searchbar.';

const USER_SEARCH_ERROR = 'An error occurred trying to search users. Please try again later.';

const GROUP_SEARCH_ERROR = 'An error occurred trying to search groups. Please try again later.';

const INVALID_LOGIN_ERROR = 'Invalid username or password.';

const INVALID_PASSWORD_ERROR = 'Invalid password.';

const INTERNATIONAL_ADDRESS_ERROR = 'RWB shirts are only available for shipping to US locations. If you have a domestic address, please update it for shipping to receive your red shirt.';

const ADDRESS_VERIFICATION_ERROR = 'Could not verify address. Please submit your address via manual entry.';

const JOIN_CHALLENGE_ERROR = 'Unable to join this challenge. Please try again later.';

const LOAD_CHALLENGE_ERROR = 'Unable to load this challenge. Please try again later.';

const GENERIC_ERROR = 'There was an error contacting the server. Please try again.';

const CONTACT_SUPPORT_ERROR = `Server error. Please try again. If the problem persists, please contact ${projectConfig.supportEmail}`;

const RETRIEVING_USER_ERROR = 'There was an error retrieving the user.';

const PRIVATE_USER_ERROR = 'This user has set their profile to private.';

const ANDROID_PHOTO_LIBRARY_ERROR = `${projectConfig.name} needs your permission to access your photo library.\nPlease change your permissions in Android's settings.`;

const CAMERA_ROLL_ERROR = 'There was a problem accessing your camera roll.';

const NO_ADDRESSES_ERROR = "We couldn't find any addresses near your location.";

const UPLOADING_IMAGE_ERROR = `There was an issue uploading your image to ${projectConfig.name}'s server. Please try again`;

const REPORT_SENT = 'Your report has been sent';

const MISSING_SF_KEY = `No salesforce contact key. Please contact ${projectConfig.name}.`;

const CANNOT_GET_ATTENDEES_CSV = 'Cannot obtain the attendees CSV. Please try again.'

const GROUP_ERRORS = {
  LOAD: 'Unable to load the group. Please try again later.',
  JOIN: 'Unable to join the group at this time. Please try again later',
  LEAVE: 'Unable to leave the group at this time. Please try again later',
};

const LOCATION_ERRORS = {
  PERMISSION: `You have not given ${projectConfig.name} permission to access your location.\nPlease enter your settings and allow us to access your location.`,
  NO_LOCATION: `${projectConfig.name} couldn't find your location. Please make sure location services are enabled on this device.`, // mobile
  GENERAL:
    'An error occurred trying to find your location. Please try again later.',
  NOT_FOUND: 'Location cannot be found.', //web
};

const EVENT_ERRORS = {
  IMAGE_UPLOAD: `Error uploading event image to ${projectConfig.name} Servers.`,
  UPDATING_ERROR: `Error uploading updated event to ${projectConfig.name} Servers.`,
  CREATING_ERROR: `Error uploading event to ${projectConfig.name} Servers.`,
  REST_ATTENDEES:
    'There was an error fetching the rest of the event attendees.',
  ATTENDEE_RETRIES:
    'There was a problem with the server. Some event attendees will not be displayed.',
  ATTENDESS: 'There was an error loading event attendees.',
  INTERESTED_AND_GOING_USERS: 'Could not display interested and going users',
  SOME_INTERESTED_AND_GOING:
    'Could not display some interested and going users',
  FETCH_ATTENDANCE:
    "Sorry, we couldn't fetch your attendance status for this event. You are still able to check-in or mark yourself as going or interested in this event.",
  REMOVING: 'There was an error removing the event.',
  ACCOUNT_NOT_SYNCED:
    'Unable to create an event until your account has fully synced. Try again in 15 minutes.',
  CANCELED_AND_REMOVED:
    'This event you are looking for has been canceled and removed.',
};

const CHALLENGE_ERRORS = {
  JOIN: 'Unable to join this challenge. Please try again later.',
  LOAD: 'Unable to load this challenge. Please try again later.',
  LOAD_PARTICIPANTS: 'Error Loading Challenge Participants',
  RANK: 'Error Loading User Ranking',
  LEADERBOARD: 'Error Loading Leaderboard Top 25',
  FEED: 'Error Loading Challenge Feed',
  LEAVE: 'Unable to leave this challenge. Please try again later.',
  LEADERBOARD_CENTERED: 'Error Loading Leaderboard Centered 25',
  RECORDING_WORKOUT: 'Error Recording Workout',
  DELETING_WORKOUT: 'Error Deleting Workout',
  LOADING_WORKOUT: 'Error Loading Workout',
  LOADING_WORKOUTS: 'Error Loading Workouts',
};

const ACCOUNT_ERRORS = {
  UNVERIFIED: `Please check your email inbox for a link from ${projectConfig.name}. If you're still having trouble, contact support.`,
  REMOVE_PROFILE_PHOTO:
    'There was a problem removing your profile photo. Please try again in a minute.',
  REMOVE_COVER_PHOTO:
    'There was a problem removing your cover photo. Please try again in a minute.',
  UPLOADING_PHOTO:
    'There was a problem uploading your photo. Add a photo at any time from "My Profile."',
  UPLOADING_PHOTO_AND_PROFILE:
    'There was a problem uploading your photo and your profile.',
  UPLOADING_PROFILE: 'There was a problem uploading your profile.',
  SELECT_COUNTRY: 'You must select a country first.',
  UPDATING_PERSONAL_INFO:
    'There was a problem updating your personal info. Please try again later.',
  NOT_FOUND: 'Your account is not found or authorized',
  CHAPTERS: 'There was an error finding a list of available chapters or groups.',
  USER_INFORMATION: 'Issue retrieving user information',
  PASSWORD_SUCCESSFULLY_UPDATED: 'Password Updated successfully.',
  PASSWORD_UPDATE_FAILED:
    'There was an error updating your password. Please try again later.',
};

const LOGIN_ERRORS = {
  GOOGLE_PLAY_SERVICES:
    'Your Google Play Services are not available or outdated. Please update Google Play Services and try again.',
  FACEBOOK_PERMISSION: `${projectConfig.name} requires permission to access both your email and your public profile to continue.`,
  GOOGLE_DEBUG: `You should only see this if you're in debug on Android. This feature doesn't work in Android debug builds. If you're seeing this anyways, please report it to ${projectConfig.name}.`,
};

const INVALID_CONFIRMATION_CODE = 'Code is invalid. Please try again.';
const INVALID_ACCESS_CODE = 'Code is invalid. Please try again.';

export {
  STREAM_ERRORS,
  LOAD_PROFILE_ERROR,
  REPORT_ERROR,
  GET_VERSION_ERROR,
  GROUP_ERRORS,
  NO_USERS_FOUND,
  NO_GROUPS_FOUND,
  USER_SEARCH_ERROR,
  GROUP_SEARCH_ERROR,
  INVALID_LOGIN_ERROR,
  NO_GROUPS_TIP,
  INTERNATIONAL_ADDRESS_ERROR,
  ADDRESS_VERIFICATION_ERROR,
  JOIN_CHALLENGE_ERROR,
  LOAD_CHALLENGE_ERROR,
  CHALLENGE_ERRORS,
  ACCOUNT_ERRORS,
  GENERIC_ERROR,
  CONTACT_SUPPORT_ERROR,
  RETRIEVING_USER_ERROR,
  PRIVATE_USER_ERROR,
  EVENT_ERRORS,
  ANDROID_PHOTO_LIBRARY_ERROR,
  CAMERA_ROLL_ERROR,
  NO_ADDRESSES_ERROR,
  LOCATION_ERRORS,
  UPLOADING_IMAGE_ERROR,
  LOGIN_ERRORS,
  REPORT_SENT,
  INVALID_CONFIRMATION_CODE,
  INVALID_ACCESS_CODE,
  MISSING_SF_KEY,
  INVALID_PASSWORD_ERROR,
  POST_BLOCK_ERROR,
  POST_DELETE_ERROR,
  CANNOT_GET_ATTENDEES_CSV
};
